import { useEffect, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CommunityProject, CommunityProjectMilestone, Page, ParticipationCampaign } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, Spinner, StatusBadge } from '../../ui/components';

/**
 * Full community-project lifecycle: PROPOSED -> UNDER_REVIEW -> SHORTLISTED
 * (attached to a DRAFT/OPEN campaign) -> SELECTED|NOT_SELECTED (campaign
 * finalize, see campaigns/CampaignDetail.tsx) -> IN_DELIVERY -> COMPLETED,
 * with REJECTED reachable from PROPOSED/UNDER_REVIEW and CANCELLED from
 * SELECTED/IN_DELIVERY — exactly the state machine documented at the top of
 * m41-local-government's prisma/schema.prisma. Every button below calls the
 * real transition endpoint; there is no client-side state faking.
 */
export function ProjectDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const isAdmin = persona?.roles.includes('DISTRICT_ADMINISTRATOR') ?? false;

  const projectQ = useApiQuery<CommunityProject>(`/api/v1/m41/projects/${id}`);
  const project = projectQ.data;

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showShortlist, setShowShortlist] = useState(false);
  const [showDeliver, setShowDeliver] = useState(false);
  const [m20Ref, setM20Ref] = useState('');

  const refresh = () => {
    projectQ.reload();
    onChanged();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const startReview = () => run(() => api.post(`/api/v1/m41/projects/${id}/start-review`));
  const reject = () =>
    run(async () => {
      await api.post(`/api/v1/m41/projects/${id}/reject`, { reason: rejectReason.trim() });
      setShowReject(false);
      setRejectReason('');
    });
  const cancelProject = () =>
    run(async () => {
      await api.post(`/api/v1/m41/projects/${id}/cancel`, { reason: cancelReason.trim() });
      setShowCancel(false);
      setCancelReason('');
    });
  const deliver = () =>
    run(async () => {
      await api.post(`/api/v1/m41/projects/${id}/deliver`, m20Ref.trim() ? { m20ProjectRef: m20Ref.trim() } : undefined);
      setShowDeliver(false);
      setM20Ref('');
    });
  const complete = () => run(() => api.post(`/api/v1/m41/projects/${id}/complete`));

  return (
    <Modal title={project ? project.title : 'Project'} onClose={onClose} wide>
      {projectQ.loading ? <Spinner /> : null}
      {projectQ.error ? <ErrorNote message={projectQ.error} /> : null}
      {actionError ? (
        <div className="mb-3">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {project ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={project.status} />
            <span className="text-xs text-slate-500">{project.refNo}</span>
            <span className="text-xs text-slate-500">· {project.category.replaceAll('_', ' ')}</span>
          </div>
          <p className="text-sm text-slate-700">{project.description}</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-slate-500">Estimated cost</dt>
              <dd className="font-medium text-slate-800">{scr(project.estimatedCostScr)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Allocated</dt>
              <dd className="font-medium text-slate-800">{project.allocatedScr != null ? scr(project.allocatedScr) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Votes</dt>
              <dd className="font-medium text-slate-800">{project.voteCount}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Proposed by</dt>
              <dd className="font-mono text-xs text-slate-800">{project.proposedBy}</dd>
            </div>
          </dl>

          {project.status === 'REJECTED' && project.rejectionReason ? <ErrorNote message={`Rejected: ${project.rejectionReason}`} /> : null}
          {project.status === 'CANCELLED' && project.cancelReason ? <ErrorNote message={`Cancelled: ${project.cancelReason}`} /> : null}
          {project.status === 'NOT_SELECTED' ? (
            <p className="text-xs text-slate-500">Not selected in its campaign's vote-ranked funding round.</p>
          ) : null}
          {project.m20ProjectRef ? <p className="text-xs text-slate-500">M20 reference: {project.m20ProjectRef}</p> : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {project.status === 'PROPOSED' ? (
              <>
                <button className="btn-primary" disabled={busy} onClick={startReview}>
                  Start review
                </button>
                <button className="btn-danger" disabled={busy} onClick={() => setShowReject(true)}>
                  Reject
                </button>
              </>
            ) : null}
            {project.status === 'UNDER_REVIEW' ? (
              <>
                {isAdmin ? (
                  <button className="btn-primary" disabled={busy} onClick={() => setShowShortlist(true)}>
                    Shortlist to campaign…
                  </button>
                ) : null}
                <button className="btn-danger" disabled={busy} onClick={() => setShowReject(true)}>
                  Reject
                </button>
              </>
            ) : null}
            {project.status === 'SHORTLISTED' ? (
              <p className="text-xs text-slate-500">
                Attached to a participatory-budget campaign — open the Participatory Budget screen to vote or finalize
                it.
              </p>
            ) : null}
            {project.status === 'SELECTED' && isAdmin ? (
              <button className="btn-primary" disabled={busy} onClick={() => setShowDeliver(true)}>
                Start delivery…
              </button>
            ) : null}
            {project.status === 'IN_DELIVERY' && isAdmin ? (
              <>
                <button className="btn-success" disabled={busy} onClick={complete}>
                  Complete project
                </button>
                <button className="btn-danger" disabled={busy} onClick={() => setShowCancel(true)}>
                  Cancel project
                </button>
              </>
            ) : null}
          </div>

          {showReject ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <Field label="Rejection reason">
                <textarea className="field-input" rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowReject(false)}>
                  Back
                </button>
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={reject}>
                  Confirm reject
                </button>
              </div>
            </div>
          ) : null}
          {showCancel ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <Field label="Cancellation reason">
                <textarea className="field-input" rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowCancel(false)}>
                  Back
                </button>
                <button className="btn-danger" disabled={busy || cancelReason.trim().length < 3} onClick={cancelProject}>
                  Confirm cancel
                </button>
              </div>
            </div>
          ) : null}
          {showDeliver ? (
            <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50 p-3">
              <Field label="M20 Infrastructure project reference (optional)">
                <input className="field-input" value={m20Ref} onChange={(e) => setM20Ref(e.target.value)} placeholder="e.g. M20-WRK-2026-00042" />
              </Field>
              <p className="text-xs text-slate-500">
                M41 has no live SDK client into M20 Infrastructure yet — this is stored as a loose reference, not
                actually forwarded.
              </p>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowDeliver(false)}>
                  Back
                </button>
                <button className="btn-primary" disabled={busy} onClick={deliver}>
                  Confirm start delivery
                </button>
              </div>
            </div>
          ) : null}
          {showShortlist ? (
            <ShortlistPicker
              projectId={id}
              district={district}
              onCancel={() => setShowShortlist(false)}
              onDone={() => {
                setShowShortlist(false);
                refresh();
              }}
            />
          ) : null}

          {project.status === 'IN_DELIVERY' || (project.milestones?.length ?? 0) > 0 ? (
            <MilestonesPanel
              projectId={id}
              milestones={project.milestones ?? []}
              isDelivering={project.status === 'IN_DELIVERY'}
              onChanged={refresh}
            />
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function ShortlistPicker({
  projectId,
  district,
  onCancel,
  onDone,
}: {
  projectId: string;
  district: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const campaignsQ = useApiQuery<Page<ParticipationCampaign>>(`/api/v1/m41/campaigns?district=${encodeURIComponent(district)}&limit=100`);
  const eligible = (campaignsQ.data?.items ?? []).filter((c) => c.status === 'DRAFT' || c.status === 'OPEN');
  const [campaignId, setCampaignId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!campaignId && eligible.length > 0) setCampaignId(eligible[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible.length]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m41/projects/${projectId}/shortlist`, { campaignId });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to shortlist');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
      {error ? <ErrorNote message={error} /> : null}
      {campaignsQ.loading ? (
        <Spinner label="Loading campaigns…" />
      ) : eligible.length === 0 ? (
        <p className="text-xs text-slate-600">
          No DRAFT or OPEN participatory-budget campaign exists in this district yet — create one on the Participatory
          Budget screen first.
        </p>
      ) : (
        <Field label="Campaign">
          <select className="field-input" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            {eligible.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} ({c.status})
              </option>
            ))}
          </select>
        </Field>
      )}
      <div className="flex justify-end gap-2">
        <button className="btn-secondary" onClick={onCancel}>
          Back
        </button>
        <button className="btn-primary" disabled={busy || !campaignId} onClick={submit}>
          {busy ? 'Shortlisting…' : 'Confirm shortlist'}
        </button>
      </div>
    </div>
  );
}

function MilestonesPanel({
  projectId,
  milestones,
  isDelivering,
  onChanged,
}: {
  projectId: string;
  milestones: CommunityProjectMilestone[];
  isDelivering: boolean;
  onChanged: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (milestoneId: string, kind: 'complete' | 'miss') => {
    setBusyId(milestoneId);
    setError(null);
    try {
      await api.post(`/api/v1/m41/projects/${projectId}/milestones/${milestoneId}/${kind}`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update milestone');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Delivery milestones</h3>
        {isDelivering ? (
          <button className="btn-secondary" onClick={() => setShowAdd(true)}>
            + Add milestone
          </button>
        ) : null}
      </div>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {milestones.length === 0 ? (
        <EmptyState title="No milestones yet" hint={isDelivering ? 'Add one to track delivery progress.' : 'Milestones can only be added while IN_DELIVERY.'} />
      ) : (
        <ul className="space-y-2">
          {milestones.map((m) => (
            <li key={m.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-800">{m.title}</div>
                <div className="text-xs text-slate-500">
                  Planned {formatDate(m.plannedDate)}
                  {m.notes ? ` · ${m.notes}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={m.status} />
                {m.status === 'PENDING' && isDelivering ? (
                  <>
                    <button
                      className="text-xs font-semibold text-seagreen-600 hover:underline disabled:opacity-50"
                      disabled={busyId === m.id}
                      onClick={() => act(m.id, 'complete')}
                    >
                      Met
                    </button>
                    <button
                      className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-50"
                      disabled={busyId === m.id}
                      onClick={() => act(m.id, 'miss')}
                    >
                      Missed
                    </button>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {showAdd ? (
        <AddMilestoneModal
          projectId={projectId}
          onClose={() => setShowAdd(false)}
          onDone={() => {
            setShowAdd(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function AddMilestoneModal({ projectId, onClose, onDone }: { projectId: string; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [plannedDate, setPlannedDate] = useState(() => new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m41/projects/${projectId}/milestones`, {
        title: title.trim(),
        description: description.trim() || undefined,
        plannedDate,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to add milestone');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add delivery milestone" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Foundation works complete" />
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Planned date">
          <input className="field-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || title.trim().length < 2} onClick={submit}>
            {busy ? 'Adding…' : 'Add milestone'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
